import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
} from '@chakra-ui/react';
import {
  FormTranslationKeys,
  ROOMMATE_SELECTION_CHANGE_TRANSLATIONS,
  SystemTranslationKeys,
} from '@common/entities';

import { useSystemTranslation } from 'hooks/useSystemTranslation';

const ConfirmationContext = createContext<(pageId: string) => Promise<boolean>>(async () => false);

export const useRoommateSelectionConfirmation = () => useContext(ConfirmationContext);

export function RoommateSelectionConfirmationProvider({ children }: React.PropsWithChildren) {
  const [pageId, setPageId] = useState<string>();
  const resolver = useRef<((confirmed: boolean) => void) | undefined>();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const { tSystem } = useSystemTranslation();
  const location = useLocation();
  const settle = useCallback((confirmed: boolean) => {
    resolver.current?.(confirmed);
    resolver.current = undefined;
    setPageId(undefined);
  }, []);
  useEffect(
    () => () => {
      resolver.current?.(false);
    },
    [],
  );
  useEffect(() => {
    settle(false);
  }, [location.key, settle]);
  const confirm = useCallback(
    (id: string) =>
      new Promise<boolean>((resolve) => {
        // Two navigation controls can request the same save. Never replace a waiting decision.
        if (resolver.current) {
          resolve(false);
          return;
        }
        resolver.current = resolve;
        setPageId(id);
      }),
    [],
  );
  return (
    <ConfirmationContext.Provider value={confirm}>
      {children}
      <AlertDialog isOpen={!!pageId} leastDestructiveRef={cancelRef} onClose={() => settle(false)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>
              {t(FormTranslationKeys.page.roommateSelectionChangeTitle(pageId ?? ''), {
                defaultValue: ROOMMATE_SELECTION_CHANGE_TRANSLATIONS.roommateSelectionChangeTitle,
              })}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t(FormTranslationKeys.page.roommateSelectionChangeMessage(pageId ?? ''), {
                defaultValue: ROOMMATE_SELECTION_CHANGE_TRANSLATIONS.roommateSelectionChangeMessage,
              })}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => settle(false)}>
                {tSystem(SystemTranslationKeys.common.cancel)}
              </Button>
              <Button ml={3} onClick={() => settle(true)}>
                {tSystem(SystemTranslationKeys.common.yes)}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </ConfirmationContext.Provider>
  );
}
